import { type FuelCardMatching, FuelCardRequester } from '@cobliteam/api-lib';
import type { FuelCardVehicle } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import * as ActionCreators from '@cobliteam/store/src/FuelCard/actionCreators';
import type * as Action from '@cobliteam/store/src/FuelCard/actions';
import { FuelCardActionTypes } from '@cobliteam/store/src/FuelCard/fuelCardActionTypes';
import Alert from '@cobliteam/ui/components/Alert';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { all, call, put, takeLatest } from 'redux-saga/effects';

const env = Environment.getInstance().getEnvironment();
const fuelCardRequester = new FuelCardRequester({ host: env.herbieApiHost });

const downloadReportFile = (blob: Blob, name: string, startInMilli: number, endInMilli: number): void => {
  const startTimeStr = moment(startInMilli).format('YYMMDD');
  const endTimeStr = moment(endInMilli).format('YYMMDD');
  const periodStr = startTimeStr === endTimeStr ? startTimeStr : `${startTimeStr}_${endTimeStr}`;

  const filename = `Cobli_${periodStr}_${name}.xlsx`;
  saveAs(blob, filename);
};

export function* requestDownloadTransactionsReportSaga(action: Action.DownloadTransactionsReportRequest) {
  const { period, vehicleGroupPaths } = action.payload;

  try {
    const reportBlob: Blob = yield call(fuelCardRequester.getTransactionsReport, period, vehicleGroupPaths);
    downloadReportFile(reportBlob, 'transacoes', period.start, period.end);
  } catch (err) {
    const msg = 'Ops! O sistema apresentou um erro e não foi possível exportar o relatório, tente novamente.';
    Alert.error(msg);
  } finally {
    yield put(ActionCreators.downloadTransactionsReportEnd());
  }
}

export function* requestLoadFuelVehiclesSaga() {
  try {
    const fuelVehicles: FuelCardVehicle[] = yield call(fuelCardRequester.getFuelVehicles);
    yield put(ActionCreators.loadFuelVehiclesSucceeded(fuelVehicles));
  } catch (err) {
    yield put(ActionCreators.loadFuelVehiclesFailed());
  }
}

export function* requestUpdateFuelVehiclesSaga(action: Action.UpdateFuelVehiclesRequest) {
  try {
    yield call(fuelCardRequester.updateFuelVehicles, action.payload);
    Alert.success('Alterações salvas com sucesso!');
  } catch (err) {
    const msg = 'Ops! O sistema apresentou um erro e não foi possível salvar as alterações agora, tente novamente.';
    Alert.error(msg);
  } finally {
    yield put(ActionCreators.updateFuelVehiclesEnd());
    yield put(ActionCreators.loadFuelVehiclesRequest());
  }
}

export function* requestLoadFuelCardMatchingsSagas(action: Action.LoadFuelCardMatchingsRequest) {
  const { fuelCardStatus } = action.payload;
  try {
    const fuelCardMatchings: FuelCardMatching[] = yield call(fuelCardRequester.loadFuelCardMatchings, fuelCardStatus);
    yield put(ActionCreators.loadFuelCardMatchingsSucceeded(fuelCardMatchings));
  } catch (err) {
    yield put(ActionCreators.loadFuelCardMatchingsFailed());
    Alert.error('Houve um erro ao carregar as associações, tente novamente mais tarde.');
  }
}

export function* requestSaveFuelCardMatchingSagas(action: Action.SaveFuelCardMatchingRequest) {
  const { id, licensePlate } = action.payload;
  try {
    yield call(fuelCardRequester.saveFuelCardMatching, id, licensePlate);
    yield put(ActionCreators.saveFuelCardMatchingSucceeded(id, licensePlate));
  } catch (err) {
    yield put(ActionCreators.saveFuelCardMatchingFailed());
    Alert.error('Houve um erro ao salvar a associação, tente novamente mais tarde.');
  }
}

export function* FuelCardSagasListener() {
  yield all([
    takeLatest(FuelCardActionTypes.DOWNLOAD_TRANSACTIONS_REPORT_REQUEST, requestDownloadTransactionsReportSaga),
    takeLatest(FuelCardActionTypes.LOAD_FUEL_VEHICLES_REQUEST, requestLoadFuelVehiclesSaga),
    takeLatest(FuelCardActionTypes.UPDATE_FUEL_VEHICLES_REQUEST, requestUpdateFuelVehiclesSaga),
    takeLatest(FuelCardActionTypes.LOAD_FUEL_CARD_MATCHINGS_REQUEST, requestLoadFuelCardMatchingsSagas),
    takeLatest(FuelCardActionTypes.SAVE_FUEL_CARD_MATCHING_REQUEST, requestSaveFuelCardMatchingSagas),
  ]);
}
